import React, { useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { BasicInfo, PortfolioData } from "../types";
import { scrollToSection } from "../utils/scrollToSection";
import ThemeToggle from "./ThemeToggle";
import { generateResumePDF } from "../utils/generateResumePDF";

interface NavigationProps {
  basics: BasicInfo;
  portfolioData: PortfolioData;
}

const Navigation: React.FC<NavigationProps> = ({ basics, portfolioData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const navbarCollapseRef = useRef<HTMLDivElement>(null);
  const navbarTogglerRef = useRef<HTMLButtonElement>(null);

  const handleDownloadResume = () => {
    if (portfolioData) {
      generateResumePDF(portfolioData);
    }
  };

  useEffect(() => {
    // Function to close mobile menu
    const closeMobileMenu = () => {
      if (
        navbarCollapseRef.current &&
        navbarCollapseRef.current.classList.contains("show")
      ) {
        navbarCollapseRef.current.classList.remove("show");
        if (navbarTogglerRef.current) {
          navbarTogglerRef.current.setAttribute("aria-expanded", "false");
        }
      }
    };

    // Close menu when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      if (
        navbarCollapseRef.current &&
        navbarTogglerRef.current &&
        !navbarCollapseRef.current.contains(event.target as Node) &&
        !navbarTogglerRef.current.contains(event.target as Node)
      ) {
        closeMobileMenu();
      }
    };

    // Close menu on window resize (when switching from mobile to desktop)
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        // Bootstrap lg breakpoint
        closeMobileMenu();
      }
    };

    // Add event listeners
    document.addEventListener("click", handleClickOutside);
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => {
      document.removeEventListener("click", handleClickOutside);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleNavClick = (sectionId: string) => {
    // Close mobile menu when navigation item is clicked
    if (
      navbarCollapseRef.current &&
      navbarCollapseRef.current.classList.contains("show")
    ) {
      navbarCollapseRef.current.classList.remove("show");
      if (navbarTogglerRef.current) {
        navbarTogglerRef.current.setAttribute("aria-expanded", "false");
      }
    }

    if (sectionId === "home") {
      navigate("/home");
    } else if (sectionId === "projects") {
      // Navigate to projects page and scroll to top
      navigate("/projects");
      // Ensure we scroll to top after navigation
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 100);
    } else {
      // For experience, skills, education, awards - scroll to sections on home page
      if (location.pathname !== "/" && location.pathname !== "/home") {
        navigate(`/home#${sectionId}`);
      } else {
        scrollToSection(`#${sectionId}`);
      }
    }
  };

  const handleTogglerClick = () => {
    if (navbarCollapseRef.current) {
      const isExpanded = navbarCollapseRef.current.classList.contains("show");
      if (isExpanded) {
        navbarCollapseRef.current.classList.remove("show");
        if (navbarTogglerRef.current) {
          navbarTogglerRef.current.setAttribute("aria-expanded", "false");
        }
      } else {
        navbarCollapseRef.current.classList.add("show");
        if (navbarTogglerRef.current) {
          navbarTogglerRef.current.setAttribute("aria-expanded", "true");
        }
      }
    }
  };

  return (
    <nav className="navbar navbar-expand-lg bg-light shadow-lg">
      <div className="container">
        {/* Using Link for home to avoid full reload */}
        <Link className="navbar-brand d-flex align-items-center" to="/">
          <div className="d-flex flex-column">
            <span className="navbar-brand-text">{basics.name}</span>
            <small className="navbar-brand-text-sm">{basics.label}</small>
          </div>
        </Link>

        <button
          ref={navbarTogglerRef}
          className="navbar-toggler"
          type="button"
          onClick={handleTogglerClick}
          aria-controls="navbarNav"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div
          ref={navbarCollapseRef}
          className="collapse navbar-collapse"
          id="navbarNav"
        >
          <ul className="navbar-nav mx-auto">
            <li className="nav-item">
              <button
                className="nav-link btn btn-link"
                onClick={() => handleNavClick("home")}
                style={{
                  border: "none",
                  background: "none",
                  textDecoration: "none",
                }}
              >
                Home
              </button>
            </li>
            <li className="nav-item">
              <button
                className="nav-link btn btn-link"
                onClick={() => handleNavClick("projects")}
                style={{
                  border: "none",
                  background: "none",
                  textDecoration: "none",
                }}
              >
                Projects
              </button>
            </li>
            {/* These are for scrolling within the Home Page. They will only work if on the Home page. */}
            <li className="nav-item">
              <button
                className="nav-link btn btn-link"
                onClick={() => handleNavClick("experience")}
                style={{
                  border: "none",
                  background: "none",
                  textDecoration: "none",
                }}
              >
                Experience
              </button>
            </li>
            <li className="nav-item">
              <button
                className="nav-link btn btn-link"
                onClick={() => handleNavClick("skills")}
                style={{
                  border: "none",
                  background: "none",
                  textDecoration: "none",
                }}
              >
                Skills
              </button>
            </li>
            <li className="nav-item">
              <button
                className="nav-link btn btn-link"
                onClick={() => handleNavClick("education")}
                style={{
                  border: "none",
                  background: "none",
                  textDecoration: "none",
                }}
              >
                Education
              </button>
            </li>
            <li className="nav-item">
              <button
                className="nav-link btn btn-link"
                onClick={() => handleNavClick("awards")}
                style={{
                  border: "none",
                  background: "none",
                  textDecoration: "none",
                }}
              >
                Awards
              </button>
            </li>
            <li className="nav-item">
              <button
                className="nav-link btn btn-link"
                onClick={handleDownloadResume}
                aria-label="Download Resume as PDF"
                style={{
                  border: "none",
                  background: "none",
                  textDecoration: "none",
                }}
              >
                Resume
              </button>
            </li>
          </ul>

          {/* Theme Toggle Button */}
          <ul className="navbar-nav ms-auto">
            <li className="nav-item">
              <ThemeToggle />
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
